import { useEffect } from 'react';
import {
  useWindowFocus,
  createUseQueryHook,
  createUseMutationHook,
} from '@jupiterone/jupiter-web-toolkit';
import {
  InAppNotificationResult,
  MutationReadNotificationArgs,
  QueryInAppNotificationsArgs,
  ResolverTypeWrapper,
  InAppNotification,
} from '@jupiterone/notification-types';
import {
  InAppNotificationsQuery,
  ReadNotificationMutation,
} from 'navbar/src/notifications/graphql/queries';

const IN_APP_NOTIFICATIONS_POLLING_INTERVAL = 60 * 1000;

const getUnreadNotifications = (
  notifications: InAppNotification[]
): InAppNotification[] => notifications.filter((n) => !n.readDate);

export const useInAppNotificationsQuery = createUseQueryHook<
  { inAppNotifications: InAppNotificationResult },
  QueryInAppNotificationsArgs
>(InAppNotificationsQuery);

export const useReadNotificationMutation = createUseMutationHook<
  ResolverTypeWrapper<InAppNotification>,
  MutationReadNotificationArgs
>(ReadNotificationMutation);

const useInAppNotifications = () => {
  const {
    data,
    loading,
    refetch,
    startPolling,
    stopPolling,
  } = useInAppNotificationsQuery({
    variables: { endCursor: '' },
    pollInterval: IN_APP_NOTIFICATIONS_POLLING_INTERVAL,
  });
  const [mutate, { loading: loadingMutation }] = useReadNotificationMutation();

  const notifications = data?.inAppNotifications?.notifications || [];
  const unreadNotifications = getUnreadNotifications(notifications);

  // Poll for notifications when current window is focused
  const isWindowFocused = useWindowFocus();
  useEffect(() => {
    if (isWindowFocused) {
      startPolling(IN_APP_NOTIFICATIONS_POLLING_INTERVAL);
    } else {
      stopPolling();
    }
  }, [isWindowFocused]);

  const handleReadNotification = async (notificationId: string) => {
    const notification = notifications.find(
      (n) => n.notificationId === notificationId
    );
    if (!notification?.readDate) {
      await mutate({
        variables: {
          notificationId,
        },
      });
      void refetch();
    }
  };

  const handleReadAllUnreadNotifications = async () => {
    const unreadNotifications = getUnreadNotifications(notifications);
    if (unreadNotifications.length !== 0) {
      await Promise.all(
        unreadNotifications.map(({ notificationId }) =>
          handleReadNotification(notificationId)
        )
      );
      void refetch();
    }
  };

  return {
    loading: loading || loadingMutation,
    notifications,
    unreadNotifications,
    refetch,
    handleReadNotification,
    handleReadAllUnreadNotifications,
  };
};

export default useInAppNotifications;
